import {
  uuid,
  timestamp,
  boolean,
  numeric,
  varchar,
  jsonb,
  text,
  date,
  integer,
  json,
  bigint,
  serial,
} from 'drizzle-orm/pg-core/columns';
import { pgTable } from 'drizzle-orm/pg-core/table';
import { foreignKey } from 'drizzle-orm/pg-core/foreign-keys';
import { check } from 'drizzle-orm/pg-core/checks';
import { unique } from 'drizzle-orm/pg-core/unique-constraint';
import { uniqueIndex, index } from 'drizzle-orm/pg-core/indexes';
import { sql } from 'drizzle-orm/sql/sql';
import { companies } from './companies';
import { bytea, byteaAsBuffer } from './types-bytea';
import { users } from './users';
import { files } from './files';
import { shareholders } from './shareholders';
import { identities } from './identities';
import { warrantProgramParticipants } from './warrant_programs';
import { warrantPrograms } from './warrant_programs';

export const articlesOfAssocDocuments = pgTable(
  'articles_of_assoc_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    articlesOfAssocId: uuid('articles_of_assoc_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxAoadAoaid: index('idx_aoad_aoaid').using('btree', table.articlesOfAssocId.asc().nullsLast().op('uuid_ops')),
        idxAoadFileid: uniqueIndex('idx_aoad_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxAoaidDid: uniqueIndex('idx_aoaid_did').using(
          'btree',
          table.articlesOfAssocId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        articlesOfAssocDocumentsArticlesOfAssocIdFkey: foreignKey({
          columns: [table.articlesOfAssocId],
          foreignColumns: [articlesOfAssocs.id],
          name: 'articles_of_assoc_documents_articles_of_assoc_id_fkey',
        }),
        articlesOfAssocDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'articles_of_assoc_documents_file_id_fkey',
        }),
      },
    ];
  },
);

export const articlesOfAssocs = pgTable(
  'articles_of_assocs',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    changeName: boolean('change_name').default(false).notNull(),
    changeSpecialName: boolean('change_special_name').default(false).notNull(),
    changeAddress: boolean('change_address').default(false).notNull(),
    changeEmail: boolean('change_email').default(false).notNull(),
    changeSeat: boolean('change_seat').default(false).notNull(),
    changeCdLimits: boolean('change_cd_limits').default(false).notNull(),
    changeFiscalYear: boolean('change_fiscal_year').default(false).notNull(),
    changeCurrency: boolean('change_currency').default(false).notNull(),
    changeCds: boolean('change_cds').default(false).notNull(),
    changeSignatory: boolean('change_signatory').default(false).notNull(),
    changeAuditors: boolean('change_auditors').default(false).notNull(),
    changeNumberOfShares: boolean('change_number_of_shares').default(false).notNull(),
    numberOfSharesBefore: numeric('number_of_shares_before'),
    numberOfSharesAfter: numeric('number_of_shares_after'),
    companyId: uuid('company_id'),
    snrCaseId: uuid('snr_case_id'),
    contactId: uuid('contact_id'),
    signatoryId: uuid('signatory_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    companyName: varchar('company_name'),
    seat: varchar(),
    kindOfBusiness: varchar('kind_of_business'),
    minimumShareCapital: varchar('minimum_share_capital'),
    maximumShareCapital: varchar('maximum_share_capital'),
    minimumNumberOfShares: varchar('minimum_number_of_shares'),
    maximumNumberOfShares: varchar('maximum_number_of_shares'),
    minimumNumberOfBoardMembers: varchar('minimum_number_of_board_members'),
    maximumNumberOfBoardMembers: varchar('maximum_number_of_board_members'),
    minimumNumberOfSubstitutes: varchar('minimum_number_of_substitutes'),
    maximumNumberOfSubstitutes: varchar('maximum_number_of_substitutes'),
    summoningTimeMin: varchar('summoning_time_min'),
    summoningTimeMax: varchar('summoning_time_max'),
    agendaItemsAgmHtml: jsonb('agenda_items_agm_html'),
    fiscalYear: varchar('fiscal_year'),
    changeCompanyName: boolean('change_company_name'),
    changeKindOfBusiness: boolean('change_kind_of_business'),
    changeBoard: boolean('change_board'),
    changeNumberOfAuditors: boolean('change_number_of_auditors'),
    changeSummoning: boolean('change_summoning'),
    changeShareLimits: boolean('change_share_limits'),
    changeFisclYear: boolean('change_fiscl_year'),
    changeAgendaItems: boolean('change_agenda_items'),
    changeBoardLimits: boolean('change_board_limits'),
    newCompanyNames: jsonb('new_company_names'),
    havePostTransferPurchaseRight: boolean('have_post_transfer_purchase_right').notNull(),
    haveRightOfFirstRefusal: boolean('have_right_of_first_refusal').notNull(),
    haveConsent: boolean('have_consent').notNull(),
    haveMajority: boolean('have_majority').notNull(),
    haveArbitrator: boolean('have_arbitrator').notNull(),
    postTransferPurchaseRightPrimaryLang: text('post_transfer_purchase_right_primary_lang'),
    rightOfFirstRefusalPrimaryLang: text('right_of_first_refusal_primary_lang'),
    consentPrimaryLang: text('consent_primary_lang'),
    majorityPrimaryLang: text('majority_primary_lang'),
    arbitratorPrimaryLang: text('arbitrator_primary_lang'),
    summoningMethodEmail: boolean('summoning_method_email'),
    summoningMethodMail: boolean('summoning_method_mail').default(true).notNull(),
    summoningMethodFax: boolean('summoning_method_fax'),
    summoningMethodPoit: boolean('summoning_method_poit'),
    summoningMethodNewspaper: varchar('summoning_method_newspaper'),
    summoningMethodOtherPrimaryLang: varchar('summoning_method_other_primary_lang'),
    minimumNumberOfAuditors: varchar('minimum_number_of_auditors'),
    maximumNumberOfAuditors: varchar('maximum_number_of_auditors'),
    minimumNumberOfSubstituteAuditors: varchar('minimum_number_of_substitute_auditors'),
    maximumNumberOfSubstituteAuditors: varchar('maximum_number_of_substitute_auditors'),
    shareCapitalBefore: numeric('share_capital_before'),
    changePostTransferPurchaseRight: boolean('change_post_transfer_purchase_right').default(false).notNull(),
    changeRightOfFirstRefusal: boolean('change_right_of_first_refusal').default(false).notNull(),
    changeConsent: boolean('change_consent').default(false).notNull(),
    changeMajority: boolean('change_majority').default(false).notNull(),
    changeArbitrator: boolean('change_arbitrator').default(false).notNull(),
    digitalAgm: varchar('digital_agm').notNull(),
    changeDigitalAgm: boolean('change_digital_agm').notNull(),
    regFormPaymentThroughBolago: boolean('reg_form_payment_through_bolago').default(false),
  },
  table => {
    return [
      {
        idxAoaCid: index('idx_aoa_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        articlesOfAssocsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'articles_of_assocs_company_id_fkey',
        }),
        articlesOfAssocsContactIdFkey: foreignKey({
          columns: [table.contactId],
          foreignColumns: [identities.id],
          name: 'articles_of_assocs_contact_id_fkey',
        }),
        articlesOfAssocsSignatoryIdFkey: foreignKey({
          columns: [table.signatoryId],
          foreignColumns: [identities.id],
          name: 'articles_of_assocs_signatory_id_fkey',
        }),
        articlesOfAssocsSnrCaseIdFkey: foreignKey({
          columns: [table.snrCaseId],
          foreignColumns: [snrCases.id],
          name: 'articles_of_assocs_snr_case_id_fkey',
        }),
      },
    ];
  },
);

export const authorizationGroups = pgTable(
  'authorization_groups',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    validThruDate: date('valid_thru_date'),
    validThruAgmYear: varchar('valid_thru_agm_year'),
    snrAuthorizationId: uuid('snr_authorization_id'),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    authorizationKinds: jsonb('authorization_kinds'),
    snrCaseId: uuid('snr_case_id'),
    contactId: uuid('contact_id'),
    signatoryId: uuid('signatory_id'),
    primaryLang: boolean('primary_lang').default(true).notNull(),
    secondaryLang: boolean('secondary_lang').default(false).notNull(),
    noBoardStatement: boolean('no_board_statement').notNull(),
    regFormPaymentThroughBolago: boolean('reg_form_payment_through_bolago').default(false),
  },
  table => {
    return [
      {
        idxAgCid: index('idx_ag_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        authorizationGroupsContactIdFkey: foreignKey({
          columns: [table.contactId],
          foreignColumns: [identities.id],
          name: 'authorization_groups_contact_id_fkey',
        }),
        authorizationGroupsSignatoryIdFkey: foreignKey({
          columns: [table.signatoryId],
          foreignColumns: [identities.id],
          name: 'authorization_groups_signatory_id_fkey',
        }),
        authorizationGroupsSnrCaseIdFkey: foreignKey({
          columns: [table.snrCaseId],
          foreignColumns: [snrCases.id],
          name: 'authorization_groups_snr_case_id_fkey',
        }),
        authorizationsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'authorizations_company_id_fkey',
        }),
        authorizationsSnrAuthorizationIdFkey: foreignKey({
          columns: [table.snrAuthorizationId],
          foreignColumns: [snrAuthorizations.id],
          name: 'authorizations_snr_authorization_id_fkey',
        }),
        checkLangIsSet: check('check_lang_is_set', sql`primary_lang OR secondary_lang`),
        chkValidThruAgmYearPattern: check(
          'chk_valid_thru_agm_year_pattern',
          sql`(valid_thru_agm_year)::text ~ similar_escape('\d{4}'::text, NULL::text)`,
        ),
        chkValidThruValueSet: check(
          'chk_valid_thru_value_set',
          sql`NOT ((valid_thru_date IS NOT NULL) AND (valid_thru_agm_year IS NOT NULL))`,
        ),
      },
    ];
  },
);

export const authorizationGroupDocuments = pgTable(
  'authorization_group_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    authorizationGroupId: uuid('authorization_group_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxAgdAgid: index('idx_agd_agid').using('btree', table.authorizationGroupId.asc().nullsLast().op('uuid_ops')),
        idxAgdAgidDid: uniqueIndex('idx_agd_agid_did').using(
          'btree',
          table.authorizationGroupId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        idxAgdFileid: uniqueIndex('idx_agd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        authorizationGroupDocumentsAuthorizationGroupIdFkey: foreignKey({
          columns: [table.authorizationGroupId],
          foreignColumns: [authorizationGroups.id],
          name: 'authorization_group_documents_authorization_group_id_fkey',
        }),
        authorizationGroupDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'authorization_group_documents_file_id_fkey',
        }),
      },
    ];
  },
);

export const authorizations = pgTable(
  'authorizations',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    allocation: integer(),
    kind: integer(),
    singleUse: boolean('single_use'),
    cashPayment: boolean('cash_payment'),
    offsetPayment: boolean('offset_payment'),
    inKindPayment: boolean('in_kind_payment'),
    maxRaiseByAoa: boolean('max_raise_by_aoa'),
    maxRaise: numeric('max_raise'),
    authorizationGroupId: uuid('authorization_group_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxAAgid: index('idx_a_agid').using('btree', table.authorizationGroupId.asc().nullsLast().op('uuid_ops')),
        idxAAgidKind: uniqueIndex('idx_a_agid_kind').using(
          'btree',
          table.authorizationGroupId.asc().nullsLast().op('int4_ops'),
          table.kind.asc().nullsLast().op('int4_ops'),
        ),
        authorizationsAuthorizationGroupIdFkey: foreignKey({
          columns: [table.authorizationGroupId],
          foreignColumns: [authorizationGroups.id],
          name: 'authorizations_authorization_group_id_fkey',
        }),
        chkMaxRaiseIsAoaOrManually: check(
          'chk_max_raise_is_aoa_or_manually',
          sql`((max_raise_by_aoa = false) AND (max_raise IS NOT NULL) AND (max_raise > (0)::numeric)) OR ((max_raise_by_aoa = true) AND (max_raise IS NULL))`,
        ),
      },
    ];
  },
);

export const capTables = pgTable(
  'cap_tables',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    companyId: uuid('company_id').notNull(),
    displayName: varchar('display_name').notNull(),
    recordDate: date('record_date').notNull(),
    pricePerShare: numeric('price_per_share'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    dirty: boolean().default(false).notNull(),
    unregisteredShares: text('unregistered_shares'),
  },
  table => {
    return [
      {
        idxCtCid: index('idx_ct_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        capTablesCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'cap_tables_company_id_fkey',
        }),
      },
    ];
  },
);

export const agendaItems = pgTable(
  'agenda_items',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    order: integer().notNull(),
    subjectPrimaryLang: varchar('subject_primary_lang').notNull(),
    subjectSecondaryLang: varchar('subject_secondary_lang'),
    commentPrimaryLang: text('comment_primary_lang'),
    commentSecondaryLang: text('comment_secondary_lang'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    mandatory: boolean().default(false).notNull(),
    firstGroup: boolean('first_group').default(false).notNull(),
    lastGroup: boolean('last_group').default(false).notNull(),
    kind: integer().default(0).notNull(),
    done: boolean().default(false).notNull(),
    decisionSuggestions: jsonb('decision_suggestions'),
  },
  table => {
    return [
      {
        indexAgendaItemsOnMeetingId: index('index_agenda_items_on_meeting_id').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
        ),
        fkRails2Dccb04801: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'fk_rails_2dccb04801',
        }),
      },
    ];
  },
);

export const agendaItemDecisions = pgTable(
  'agenda_item_decisions',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    order: integer().notNull(),
    decisionPrimaryLang: text('decision_primary_lang'),
    decisionSecondaryLang: text('decision_secondary_lang'),
    agendaItemId: uuid('agenda_item_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxAgendaItemDecisionsAiid: index('idx_agenda_item_decisions_aiid').using(
          'btree',
          table.agendaItemId.asc().nullsLast().op('uuid_ops'),
        ),
        agendaItemDecisionsAgendaItemIdFkey: foreignKey({
          columns: [table.agendaItemId],
          foreignColumns: [agendaItems.id],
          name: 'agenda_item_decisions_agenda_item_id_fkey',
        }),
        uniqueAiOrder: unique('unique_ai_order').on(table.order, table.agendaItemId),
        checkADecisionIsMade: check(
          'check_a_decision_is_made',
          sql`(decision_primary_lang IS NOT NULL) OR (decision_secondary_lang IS NOT NULL)`,
        ),
      },
    ];
  },
);

export const authKeys = pgTable(
  'auth_keys',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    displayName: varchar('display_name').notNull(),
    authKey: varchar('auth_key').notNull(),
    lastUsed: timestamp('last_used', { mode: 'string' }),
    userId: uuid('user_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxUidAk: uniqueIndex('idx_uid_ak').using(
          'btree',
          table.userId.asc().nullsLast().op('text_ops'),
          table.authKey.asc().nullsLast().op('text_ops'),
        ),
        authKeysUserIdFkey: foreignKey({
          columns: [table.userId],
          foreignColumns: [users.id],
          name: 'auth_keys_user_id_fkey',
        }),
        authKeysAuthKeyCheck: check('auth_keys_auth_key_check', sql`length((auth_key)::text) >= 4`),
        authKeysAuthKeyCheck1: check('auth_keys_auth_key_check1', sql`length((auth_key)::text) >= 32`),
      },
    ];
  },
);

export const capTableStakes = pgTable(
  'cap_table_stakes',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    capTableId: uuid('cap_table_id').notNull(),
    identityId: uuid('identity_id').notNull(),
    warrantParticipantId: uuid('warrant_participant_id'),
    name: varchar().notNull(),
    kind: integer().notNull(),
    stakes: numeric(),
    stakesManual: numeric('stakes_manual'),
    floor: numeric(),
    cap: numeric(),
    investedAmount: numeric('invested_amount'),
    manualEntry: boolean('manual_entry').notNull(),
    enabled: boolean().default(true).notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    comment: text(),
    acqCost: text('acq_cost'),
    wiseQuantity: text('wise_quantity'),
  },
  table => {
    return [
      {
        idxCtsCtid: index('idx_cts_ctid').using('btree', table.capTableId.asc().nullsLast().op('uuid_ops')),
        capTableStakesCapTableIdFkey: foreignKey({
          columns: [table.capTableId],
          foreignColumns: [capTables.id],
          name: 'cap_table_stakes_cap_table_id_fkey',
        }),
        capTableStakesIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'cap_table_stakes_identity_id_fkey',
        }),
        capTableStakesWarrantParticipantIdFkey: foreignKey({
          columns: [table.warrantParticipantId],
          foreignColumns: [warrantProgramParticipants.id],
          name: 'cap_table_stakes_warrant_participant_id_fkey',
        }),
        stakesCheck: check('stakes_check', sql`(stakes IS NOT NULL) OR (stakes_manual IS NOT NULL)`),
      },
    ];
  },
);

export const authorizationMeetings = pgTable(
  'authorization_meetings',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    authorizationId: uuid('authorization_id').notNull(),
  },
  table => {
    return [
      {
        authorizationMeetingsAuthorizationIdFkey: foreignKey({
          columns: [table.authorizationId],
          foreignColumns: [authorizationGroups.id],
          name: 'authorization_meetings_authorization_id_fkey',
        }),
        authorizationMeetingsMeetingIdFkey: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'authorization_meetings_meeting_id_fkey',
        }),
      },
    ];
  },
);

export enum BM {
  /** Styrelseordförande */
  Of = 'of',
  /** VD */
  Vd = 'vd',
  /** Suppleant */
  Su = 'su',
  /** Styrelseledamot */
  Le = 'le',
  /** Vice VD */
  Vvd = 'vvd',
  /** Extern VD */
  Evd = 'evd',
  /** Extern Vice VD */
  Evvd = 'evvd',
  /** Auditor */
  Rev = 'rev',
  /** Head auditor */
  Revh = 'revh',
}

export interface CDFunction {
  id: string;
  code: BM;
  kind: string;
  accountant: string;
}

export const companyDirectors = pgTable(
  'company_directors',
  {
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    functions: jsonb().$type<CDFunction[]>().notNull(),
    registeredAbroad: boolean('registered_abroad').notNull(),
    companyId: uuid('company_id'),
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    identityId: uuid('identity_id'),
    invited: boolean().default(false).notNull(),
  },
  table => {
    return [
      {
        indexCompanyDirectorsOnCompanyId: index('index_company_directors_on_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        companyDirectorsIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'company_directors_identity_id_fkey',
        }),
        fkRails794E215E7E: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'fk_rails_794e215e7e',
        }),
      },
    ];
  },
);

export const companySuggestions = pgTable(
  'company_suggestions',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    name: varchar().notNull(),
    organizationNumber: varchar('organization_number').notNull(),
    userId: uuid('user_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    function: varchar().default('').notNull(),
    functions: jsonb().notNull(),
  },
  table => {
    return [
      {
        fkRails8054407923: foreignKey({
          columns: [table.userId],
          foreignColumns: [users.id],
          name: 'fk_rails_8054407923',
        }),
      },
    ];
  },
);

export const companySubscriptions = pgTable(
  'company_subscriptions',
  {
    id: uuid()
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    stripeSubId: varchar('stripe_sub_id').notNull(),
    status: varchar().notNull(),
    products: jsonb().notNull(),
    raw: jsonb().notNull(),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    currentPeriodStart: timestamp('current_period_start', { mode: 'string' }),
    currentPeriodEnd: timestamp('current_period_end', { mode: 'string' }),
  },
  table => {
    return [
      {
        idxCompanySubsStripeSubId: uniqueIndex('idx_company_subs_stripe_sub_id').using(
          'btree',
          table.stripeSubId.asc().nullsLast().op('text_ops'),
        ),
        idxSubscriptionsCompanyId: index('idx_subscriptions_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        companySubscriptionsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'company_subscriptions_company_id_fkey',
        }),
      },
    ];
  },
);

export const companyRefreshes = pgTable('company_refreshes', {
  id: uuid()
    .default(sql`public.gen_random_uuid()`)
    .primaryKey()
    .notNull(),
  organizationNumber: varchar('organization_number'),
  createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  done: boolean(),
  fail: text(),
});

export const cooptedMeetingParticipants = pgTable(
  'coopted_meeting_participants',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    invited: boolean().notNull(),
    companyId: uuid('company_id').notNull(),
    identityId: uuid('identity_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxCidIid: uniqueIndex('idx_cid_iid').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        cooptedMeetingParticipantsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'coopted_meeting_participants_company_id_fkey',
        }),
        cooptedMeetingParticipantsIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'coopted_meeting_participants_identity_id_fkey',
        }),
      },
    ];
  },
);

export const defaultAgendaItems = pgTable(
  'default_agenda_items',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    order: integer().notNull(),
    subjectPrimaryLang: varchar('subject_primary_lang').notNull(),
    subjectSecondaryLang: varchar('subject_secondary_lang').notNull(),
    decisionPrimaryLang: text('decision_primary_lang'),
    decisionSecondaryLang: text('decision_secondary_lang'),
    mandatory: boolean().default(false).notNull(),
    firstGroup: boolean('first_group').default(false).notNull(),
    lastGroup: boolean('last_group').default(false).notNull(),
    addAuto: boolean('add_auto').default(false).notNull(),
    kind: integer().default(0).notNull(),
    meetingKind: integer('meeting_kind').default(0).notNull(),
    decisionSuggestions: jsonb('decision_suggestions'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    commentPrimaryLang: text('comment_primary_lang'),
    commentSecondaryLang: text('comment_secondary_lang'),
  },
  table => {
    return [
      {
        indexDefaultAgendaItemsOnMeetingType: index('index_default_agenda_items_on_meeting_type').using(
          'btree',
          table.meetingKind.asc().nullsLast().op('int4_ops'),
        ),
        indexDefaultAgendaItemsOnMeetingTypeAndOrder: uniqueIndex(
          'index_default_agenda_items_on_meeting_type_and_order',
        ).using(
          'btree',
          table.meetingKind.asc().nullsLast().op('int4_ops'),
          table.order.asc().nullsLast().op('int4_ops'),
        ),
      },
    ];
  },
);

export const documentAppendices = pgTable(
  'document_appendices',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    fileId: uuid('file_id').notNull(),
    appendixId: uuid('appendix_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxDocumentAppendicesDidAid: uniqueIndex('idx_document_appendices_did_aid').using(
          'btree',
          table.fileId.asc().nullsLast().op('uuid_ops'),
          table.appendixId.asc().nullsLast().op('uuid_ops'),
        ),
        documentAppendicesAppendixIdFkey: foreignKey({
          columns: [table.appendixId],
          foreignColumns: [files.id],
          name: 'document_appendices_appendix_id_fkey',
        }),
        documentAppendicesFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'document_appendices_file_id_fkey',
        }),
        checkDocumentAppendix: check('check_document_appendix', sql`file_id <> appendix_id`),
      },
    ];
  },
);

export const emails = pgTable(
  'emails',
  {
    id: uuid()
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    jobId: varchar('job_id').notNull(),
    jobState: integer('job_state').notNull(),
    toName: varchar('to_name'),
    toEmail: varchar('to_email').notNull(),
    mandrillEvent: jsonb('mandrill_event'),
    sendgridMessageEvent: jsonb('sendgrid_message_event'),
    sendgridMessageId: varchar('sendgrid_message_id'),
    emailStatus: varchar('email_status'),
    emailFailReason: varchar('email_fail_reason'),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxEmailsCompanyId: index('idx_emails_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        idxEmailsJobId: uniqueIndex('idx_emails_job_id').using('btree', table.jobId.asc().nullsLast().op('text_ops')),
        emailsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'emails_company_id_fkey',
        }),
      },
    ];
  },
);

export const ippStripePayments = pgTable(
  'ipp_stripe_payments',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    issuanceProgramParticipantId: uuid('issuance_program_participant_id').notNull(),
    stripePaymentId: uuid('stripe_payment_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        ippStripePaymentsIssuanceProgramParticipantIdFkey: foreignKey({
          columns: [table.issuanceProgramParticipantId],
          foreignColumns: [issuanceProgramParticipants.id],
          name: 'ipp_stripe_payments_issuance_program_participant_id_fkey',
        }),
        ippStripePaymentsStripePaymentIdFkey: foreignKey({
          columns: [table.stripePaymentId],
          foreignColumns: [stripePayments.id],
          name: 'ipp_stripe_payments_stripe_payment_id_fkey',
        }),
      },
    ];
  },
);

export const investmentGrants = pgTable(
  'investment_grants',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    shareholderId: uuid('shareholder_id').notNull(),
    year: varchar().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    numberOfShares: bigint('number_of_shares', { mode: 'number' }).notNull(),
    pricePerShare: numeric('price_per_share').notNull(),
    fileId: uuid('file_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxInvestmentGrantsShid: index('idx_investment_grants_shid').using(
          'btree',
          table.shareholderId.asc().nullsLast().op('uuid_ops'),
        ),
        idxInvestmentGrantsShidYear: uniqueIndex('idx_investment_grants_shid_year').using(
          'btree',
          table.shareholderId.asc().nullsLast().op('text_ops'),
          table.year.asc().nullsLast().op('text_ops'),
        ),
        investmentGrantsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'investment_grants_file_id_fkey',
        }),
        investmentGrantsShareholderIdFkey: foreignKey({
          columns: [table.shareholderId],
          foreignColumns: [shareholders.id],
          name: 'investment_grants_shareholder_id_fkey',
        }),
      },
    ];
  },
);

export const delayedJobs = pgTable(
  'delayed_jobs',
  {
    id: serial().primaryKey().notNull(),
    priority: integer().default(0).notNull(),
    attempts: integer().default(0).notNull(),
    handler: text().notNull(),
    lastError: text('last_error'),
    runAt: timestamp('run_at', { mode: 'string' }),
    lockedAt: timestamp('locked_at', { mode: 'string' }),
    failedAt: timestamp('failed_at', { mode: 'string' }),
    lockedBy: varchar('locked_by'),
    queue: varchar(),
    createdAt: timestamp('created_at', { mode: 'string' }),
    updatedAt: timestamp('updated_at', { mode: 'string' }),
  },
  table => {
    return [
      {
        priority: index('delayed_jobs_priority').using(
          'btree',
          table.priority.asc().nullsLast().op('int4_ops'),
          table.runAt.asc().nullsLast().op('int4_ops'),
        ),
      },
    ];
  },
);

export const issuancePrograms = pgTable(
  'issuance_programs',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    numberOfShares: bigint('number_of_shares', { mode: 'number' }).notNull(),
    pricePerShare: numeric('price_per_share').notNull(),
    currency: varchar().notNull(),
    published: boolean().default(false).notNull(),
    companyId: uuid('company_id').notNull(),
    stripePaymentConfigId: uuid('stripe_payment_config_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    startDate: timestamp('start_date', { mode: 'string' }),
    endDate: timestamp('end_date', { mode: 'string' }),
  },
  table => {
    return [
      {
        issuanceProgramsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'issuance_programs_company_id_fkey',
        }),
        issuanceProgramsStripePaymentConfigIdFkey: foreignKey({
          columns: [table.stripePaymentConfigId],
          foreignColumns: [stripePaymentConfigs.id],
          name: 'issuance_programs_stripe_payment_config_id_fkey',
        }),
        issuanceProgramsCurrencyCheck: check('issuance_programs_currency_check', sql`length((currency)::text) > 0`),
        issuanceProgramsNumberOfSharesCheck: check(
          'issuance_programs_number_of_shares_check',
          sql`number_of_shares > 0`,
        ),
        issuanceProgramsPricePerShareCheck: check(
          'issuance_programs_price_per_share_check',
          sql`price_per_share > (0)::numeric`,
        ),
      },
    ];
  },
);

export const juridicalEntities = pgTable(
  'juridical_entities',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    identityNumber: varchar('identity_number').notNull(),
    name: varchar().notNull(),
    address: varchar(),
    juridicalType: integer('juridical_type').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    countryCode: varchar('country_code'),
    customIdType: varchar('custom_id_type'),
    customId: varchar('custom_id'),
    email: varchar(),
    address2: varchar('address_2'),
    city: varchar(),
    postalCode: varchar('postal_code'),
    givenName: varchar('given_name'),
    identityNumberType: integer('identity_number_type').notNull(),
    snrId: varchar('snr_id'),
  },
  table => {
    return [
      {
        idxIdentityNumberTypeIdentityNumber: uniqueIndex('idx_identity_number_type_identity_number').using(
          'btree',
          table.identityNumberType.asc().nullsLast().op('int4_ops'),
          table.identityNumber.asc().nullsLast().op('text_ops'),
        ),
        idxJeIdnTypeIdn: uniqueIndex('idx_je_idn_type_idn').using(
          'btree',
          table.identityNumberType.asc().nullsLast().op('text_ops'),
          table.identityNumber.asc().nullsLast().op('int4_ops'),
        ),
        idxSnrId: uniqueIndex('idx_snr_id').using('btree', table.snrId.asc().nullsLast().op('text_ops')),
      },
    ];
  },
);

export const fidoKeys = pgTable(
  'fido_keys',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    userId: uuid('user_id').notNull(),
    displayName: varchar('display_name').notNull(),
    kind: integer().notNull(),
    creds: jsonb().notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        fidoKeysUserIdFkey: foreignKey({
          columns: [table.userId],
          foreignColumns: [users.id],
          name: 'fido_keys_user_id_fkey',
        }),
      },
    ];
  },
);

export const issuanceProgramParticipants = pgTable(
  'issuance_program_participants',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    numberOfShares: bigint('number_of_shares', { mode: 'number' }).notNull(),
    email: varchar().notNull(),
    issuanceProgramId: uuid('issuance_program_id').notNull(),
    referenceNumber: varchar('reference_number'),
    paymentMethod: integer('payment_method').default(0).notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    bankConfirmed: boolean('bank_confirmed'),
    receiptSent: boolean('receipt_sent').default(false).notNull(),
    receiptSentAt: timestamp('receipt_sent_at', { mode: 'string' }),
    identityId: uuid('identity_id'),
  },
  table => {
    return [
      {
        issuanceProgramParticipantsIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'issuance_program_participants_identity_id_fkey',
        }),
        issuanceProgramParticipantsIssuanceProgramIdFkey: foreignKey({
          columns: [table.issuanceProgramId],
          foreignColumns: [issuancePrograms.id],
          name: 'issuance_program_participants_issuance_program_id_fkey',
        }),
        issuanceProgramParticipantsEmailCheck: check(
          'issuance_program_participants_email_check',
          sql`length((email)::text) > 0`,
        ),
        issuanceProgramParticipantsNumberOfSharesCheck: check(
          'issuance_program_participants_number_of_shares_check',
          sql`number_of_shares >= 0`,
        ),
        issuanceProgramParticipantsPaymentMethodCheck: check(
          'issuance_program_participants_payment_method_check',
          sql`payment_method >= 0`,
        ),
      },
    ];
  },
);

export const optionsRegistrationSignatories = pgTable(
  'options_registration_signatories',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    identityId: uuid('identity_id').notNull(),
    optionsRegistrationId: uuid('options_registration_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxOrsOridIdentityid: uniqueIndex('idx_ors_orid_identityid').using(
          'btree',
          table.optionsRegistrationId.asc().nullsLast().op('uuid_ops'),
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        optionsRegistrationSignatoriesIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'options_registration_signatories_identity_id_fkey',
        }),
        optionsRegistrationSignatoriesOptionsRegistrationIdFkey: foreignKey({
          columns: [table.optionsRegistrationId],
          foreignColumns: [optionsRegistrations.id],
          name: 'options_registration_signatories_options_registration_id_fkey',
        }),
      },
    ];
  },
);

export const meetingParticipantShares = pgTable(
  'meeting_participant_shares',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    shareClass: jsonb('share_class'),
    numberOfShares: numeric('number_of_shares').notNull(),
    numberOfVotes: numeric('number_of_votes').notNull(),
    meetingParticipantId: uuid('meeting_participant_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        meetingParticipantSharesMeetingParticipantIdFkey: foreignKey({
          columns: [table.meetingParticipantId],
          foreignColumns: [meetingParticipants.id],
          name: 'meeting_participant_shares_meeting_participant_id_fkey',
        }),
        cUniqMpidShareClass: unique('c_uniq_mpid_share_class').on(table.shareClass, table.meetingParticipantId),
      },
    ];
  },
);

export const niceFounders = pgTable(
  'nice_founders',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    identityId: uuid('identity_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        niceFoundersIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'nice_founders_identity_id_fkey',
        }),
        niceFoundersWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'nice_founders_warrant_program_id_fkey',
        }),
      },
    ];
  },
);

export const meetingSummonsAttachments = pgTable(
  'meeting_summons_attachments',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxMsaDid: index('idx_msa_did').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxMsaMid: index('idx_msa_mid').using('btree', table.meetingId.asc().nullsLast().op('uuid_ops')),
        idxMsaMidDid: uniqueIndex('idx_msa_mid_did').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        meetingSummonsAttachmentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'meeting_summons_attachments_file_id_fkey',
        }),
        meetingSummonsAttachmentsMeetingIdFkey: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'meeting_summons_attachments_meeting_id_fkey',
        }),
      },
    ];
  },
);

export const meetingConcerns = pgTable(
  'meeting_concerns',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    shareIssueId: uuid('share_issue_id'),
    warrantProgramId: uuid('warrant_program_id'),
    approved: boolean(),
    appendixNumber: integer('appendix_number').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    employeeOptionId: uuid('employee_option_id'),
    articlesOfAssocId: uuid('articles_of_assoc_id'),
    authorizationGroupId: uuid('authorization_group_id'),
  },
  table => {
    return [
      {
        idxMAg: uniqueIndex('idx_m_ag').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.authorizationGroupId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMAoa: uniqueIndex('idx_m_aoa').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.articlesOfAssocId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMAppendix: uniqueIndex('idx_m_appendix').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.appendixNumber.asc().nullsLast().op('uuid_ops'),
        ),
        idxMSi: uniqueIndex('idx_m_si').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.shareIssueId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMWp: uniqueIndex('idx_m_wp').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.warrantProgramId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMcAgid: index('idx_mc_agid').using('btree', table.authorizationGroupId.asc().nullsLast().op('uuid_ops')),
        idxMcAoaid: index('idx_mc_aoaid').using('btree', table.articlesOfAssocId.asc().nullsLast().op('uuid_ops')),
        idxMcSiid: index('idx_mc_siid').using('btree', table.shareIssueId.asc().nullsLast().op('uuid_ops')),
        idxMcWpid: index('idx_mc_wpid').using('btree', table.warrantProgramId.asc().nullsLast().op('uuid_ops')),
        meetingConcernsArticlesOfAssocsIdFkey: foreignKey({
          columns: [table.articlesOfAssocId],
          foreignColumns: [articlesOfAssocs.id],
          name: 'meeting_concerns_articles_of_assocs_id_fkey',
        }),
        meetingConcernsAuthorizationGroupIdFkey: foreignKey({
          columns: [table.authorizationGroupId],
          foreignColumns: [authorizationGroups.id],
          name: 'meeting_concerns_authorization_group_id_fkey',
        }),
        meetingConcernsEmployeeOptionIdFkey: foreignKey({
          columns: [table.employeeOptionId],
          foreignColumns: [warrantPrograms.id],
          name: 'meeting_concerns_employee_option_id_fkey',
        }),
        meetingConcernsMeetingIdFkey: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'meeting_concerns_meeting_id_fkey',
        }),
        meetingConcernsShareIssuanceIdFkey: foreignKey({
          columns: [table.shareIssueId],
          foreignColumns: [shareIssues.id],
          name: 'meeting_concerns_share_issuance_id_fkey',
        }),
        meetingConcernsWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'meeting_concerns_warrant_program_id_fkey',
        }),
        meetingConcernsUniq: unique('meeting_concerns_uniq').on(
          table.meetingId,
          table.shareIssueId,
          table.warrantProgramId,
          table.employeeOptionId,
          table.articlesOfAssocId,
          table.authorizationGroupId,
        ),
        chkOneConcernSet: check(
          'chk_one_concern_set',
          sql`(warrant_program_id IS NOT NULL) OR (share_issue_id IS NOT NULL) OR (employee_option_id IS NOT NULL) OR (authorization_group_id IS NOT NULL) OR (articles_of_assoc_id IS NOT NULL)`,
        ),
      },
    ];
  },
);

export const meetingAgendaTemplates = pgTable(
  'meeting_agenda_templates',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    name: varchar().notNull(),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    isDefault: boolean('is_default').notNull(),
  },
  table => {
    return [
      {
        idxMatCidName: uniqueIndex('idx_mat_cid_name').using(
          'btree',
          table.companyId.asc().nullsLast().op('text_ops'),
          table.name.asc().nullsLast().op('text_ops'),
        ),
        meetingAgendaTemplatesCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'meeting_agenda_templates_company_id_fkey',
        }),
      },
    ];
  },
);

export const meetingApprovals = pgTable(
  'meeting_approvals',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    approved: boolean(),
    meetingParticipantId: uuid('meeting_participant_id').notNull(),
    meetingId: uuid('meeting_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        indexMeetingApprovalsOnMeetingId: index('index_meeting_approvals_on_meeting_id').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
        ),
        indexMeetingApprovalsOnMeetingParticipantId: index('index_meeting_approvals_on_meeting_participant_id').using(
          'btree',
          table.meetingParticipantId.asc().nullsLast().op('uuid_ops'),
        ),
        fkRails291B330Cb6: foreignKey({
          columns: [table.meetingParticipantId],
          foreignColumns: [meetingParticipants.id],
          name: 'fk_rails_291b330cb6',
        }),
        fkRails346Afd3139: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'fk_rails_346afd3139',
        }),
      },
    ];
  },
);

export const meetingDocuments = pgTable(
  'meeting_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        indexMeetingDocumentsOnDocumentIdAndMeetingId: uniqueIndex(
          'index_meeting_documents_on_document_id_and_meeting_id',
        ).using(
          'btree',
          table.fileId.asc().nullsLast().op('uuid_ops'),
          table.meetingId.asc().nullsLast().op('uuid_ops'),
        ),
        indexMeetingDocumentsOnMeetingIdAndDocumentId: uniqueIndex(
          'index_meeting_documents_on_meeting_id_and_document_id',
        ).using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        fkRails616Eb8B7A8: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'fk_rails_616eb8b7a8',
        }),
        fkRails6Bb62C2C9A: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'fk_rails_6bb62c2c9a',
        }),
      },
    ];
  },
);

export const meetingParticipants = pgTable(
  'meeting_participants',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    initials: varchar(),
    chairman: boolean().notNull(),
    secretary: boolean().notNull(),
    adjuster: boolean().notNull(),
    present: boolean().notNull(),
    summoned: boolean().default(false).notNull(),
    position: varchar(),
    numberOfShares: numeric('number_of_shares'),
    numberOfVotes: numeric('number_of_votes'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    identityId: uuid('identity_id').notNull(),
    delMandrillEvent: jsonb('__del_mandrill_event'),
    emailId: uuid('email_id'),
    emailSummonsId: uuid('email_summons_id'),
    proxyId: uuid('proxy_id'),
    isShareholder: boolean('is_shareholder').notNull(),
    delSummonsJobState: integer('__del_summons_job_state'),
    delSummonsJobId: varchar('__del_summons_job_id'),
    delSendgridMessageId: varchar('__del_sendgrid_message_id'),
    delSendgridMessageEvent: jsonb('__del_sendgrid_message_event'),
    delSummonsEmailStatus: varchar('__del_summons_email_status'),
    delSummonsEmailFailReason: varchar('__del_summons_email_fail_reason'),
    delSummonsEmail: varchar('__del_summons_email'),
    kind: text().notNull(),
  },
  table => {
    return [
      {
        idxMpMidIid: uniqueIndex('idx_mp_mid_iid').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMpSummonsEmailStatus: index('idx_mp_summons_email_status').using(
          'btree',
          table.delSummonsEmailStatus.asc().nullsLast().op('text_ops'),
        ),
        idxMpsIdentityId: index('idx_mps_identity_id').using(
          'btree',
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMpsMeetingId: index('idx_mps_meeting_id').using('btree', table.meetingId.asc().nullsLast().op('uuid_ops')),
        idxMpsProxyId: index('idx_mps_proxy_id').using('btree', table.proxyId.asc().nullsLast().op('uuid_ops')),
        fkRailsAd58214D14: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'fk_rails_ad58214d14',
        }),
        meetingParticipantsIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'meeting_participants_identity_id_fkey',
        }),
        meetingParticipantsProxyIdFkey: foreignKey({
          columns: [table.proxyId],
          foreignColumns: [identities.id],
          name: 'meeting_participants_proxy_id_fkey',
        }),
      },
    ];
  },
);

export const meetingTodos = pgTable(
  'meeting_todos',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    meetingId: uuid('meeting_id').notNull(),
    todoId: uuid('todo_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxMeetingTodosMidTid: uniqueIndex('idx_meeting_todos_mid_tid').using(
          'btree',
          table.meetingId.asc().nullsLast().op('uuid_ops'),
          table.todoId.asc().nullsLast().op('uuid_ops'),
        ),
        idxMeetingTodosTid: uniqueIndex('idx_meeting_todos_tid').using(
          'btree',
          table.todoId.asc().nullsLast().op('uuid_ops'),
        ),
        meetingTodosMeetingIdFkey: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'meeting_todos_meeting_id_fkey',
        }),
        meetingTodosTodoIdFkey: foreignKey({
          columns: [table.todoId],
          foreignColumns: [todos.id],
          name: 'meeting_todos_todo_id_fkey',
        }),
      },
    ];
  },
);

export const meetingVotes = pgTable(
  'meeting_votes',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    agendaItemId: uuid('agenda_item_id').notNull(),
    meetingParticipantId: uuid('meeting_participant_id').notNull(),
    vote: integer().notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        iMeetingVotesOnAgendaItemIdAndMpId: uniqueIndex('i_meeting_votes_on_agenda_item_id_and_mp_id').using(
          'btree',
          table.agendaItemId.asc().nullsLast().op('uuid_ops'),
          table.meetingParticipantId.asc().nullsLast().op('uuid_ops'),
        ),
        indexMeetingVotesOnAgendaItemId: index('index_meeting_votes_on_agenda_item_id').using(
          'btree',
          table.agendaItemId.asc().nullsLast().op('uuid_ops'),
        ),
      },
    ];
  },
);

export const meetings = pgTable(
  'meetings',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    number: varchar(),
    delKind: integer('__del_kind'),
    startTime: timestamp('start_time', { mode: 'string' }).notNull(),
    place: varchar().default('').notNull(),
    dateSummoned: date('date_summoned'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    companyId: uuid('company_id').notNull(),
    approvedByMeeting: uuid('approved_by_meeting'),
    locked: boolean().default(false).notNull(),
    createdByMeeting: uuid('created_by_meeting'),
    delHeldKind: integer('__del_held_kind').default(1),
    phone: varchar().default('').notNull(),
    primaryLang: boolean('primary_lang').default(true).notNull(),
    secondaryLang: boolean('secondary_lang').default(true).notNull(),
    authorizationId: uuid('authorization_id'),
    contactEmail: varchar('contact_email'),
    registerAttendance: boolean('register_attendance'),
    usesStepper: boolean('uses_stepper').notNull(),
    registerAttendanceDate: date('register_attendance_date'),
    endTime: timestamp('end_time', { mode: 'string' }),
    postalVote: boolean('postal_vote').default(false).notNull(),
    postalVoteDate: date('postal_vote_date'),
    postalVoteEmail: varchar('postal_vote_email'),
    boardsRecommendation: boolean('boards_recommendation').default(false).notNull(),
    kind: varchar().notNull(),
    heldKind: varchar('held_kind').notNull(),
  },
  table => {
    return [
      {
        idxMCid: index('idx_m_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        fkRailsAaf323746A: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'fk_rails_aaf323746a',
        }),
        meetingsAuthorizationIdFkey: foreignKey({
          columns: [table.authorizationId],
          foreignColumns: [authorizationGroups.id],
          name: 'meetings_authorization_id_fkey',
        }),
        meetingsCreatedByMeetingFkey: foreignKey({
          columns: [table.createdByMeeting],
          foreignColumns: [table.id],
          name: 'meetings_created_by_meeting_fkey',
        }),
        checkLangIsSet: check('check_lang_is_set', sql`primary_lang OR secondary_lang`),
      },
    ];
  },
);

export const meetingParticipantDocuments = pgTable(
  'meeting_participant_documents',
  {
    id: uuid().defaultRandom().notNull(),
    meetingParticipantId: uuid('meeting_participant_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        meetingParticipantDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'meeting_participant_documents_file_id_fkey',
        }),
        meetingParticipantDocumentsMeetingParticipantIdFkey: foreignKey({
          columns: [table.meetingParticipantId],
          foreignColumns: [meetingParticipants.id],
          name: 'meeting_participant_documents_meeting_participant_id_fkey',
        }),
      },
    ];
  },
);

export const meetingParticipantEmails = pgTable(
  'meeting_participant_emails',
  {
    id: uuid()
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    fileId: uuid('file_id').notNull(),
    meetingParticipantId: uuid('meeting_participant_id').notNull(),
    emailId: uuid('email_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxMpeEmailId: uniqueIndex('idx_mpe_email_id').using('btree', table.emailId.asc().nullsLast().op('uuid_ops')),
        idxMpeMpid: index('idx_mpe_mpid').using('btree', table.meetingParticipantId.asc().nullsLast().op('uuid_ops')),
        meetingParticipantEmailsDocumentIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'meeting_participant_emails_document_id_fkey',
        }),
        meetingParticipantEmailsEmailIdFkey: foreignKey({
          columns: [table.emailId],
          foreignColumns: [emails.id],
          name: 'meeting_participant_emails_email_id_fkey',
        }),
        meetingParticipantEmailsMeetingParticipantIdFkey: foreignKey({
          columns: [table.meetingParticipantId],
          foreignColumns: [meetingParticipants.id],
          name: 'meeting_participant_emails_meeting_participant_id_fkey',
        }),
      },
    ];
  },
);

export const optionsRegistrationDocuments = pgTable(
  'options_registration_documents',
  {
    id: uuid().defaultRandom().notNull(),
    optionsRegistrationId: uuid('options_registration_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxOrfdFileid: uniqueIndex('idx_orfd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxOrfdOrid: index('idx_orfd_orid').using(
          'btree',
          table.optionsRegistrationId.asc().nullsLast().op('uuid_ops'),
        ),
        idxOrfdOridFileid: uniqueIndex('idx_orfd_orid_fileid').using(
          'btree',
          table.optionsRegistrationId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        optionsRegistrationDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'options_registration_documents_file_id_fkey',
        }),
        optionsRegistrationDocumentsOptionsRegistrationIdFkey: foreignKey({
          columns: [table.optionsRegistrationId],
          foreignColumns: [optionsRegistrations.id],
          name: 'options_registration_documents_options_registration_id_fkey',
        }),
      },
    ];
  },
);

export const optionsSharesRegistrationDocuments = pgTable(
  'options_shares_registration_documents',
  {
    id: uuid().defaultRandom().notNull(),
    optionsSharesRegistrationId: uuid('options_shares_registration_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxOsrfdFileid: uniqueIndex('idx_osrfd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxOsrfdOsrid: index('idx_osrfd_osrid').using(
          'btree',
          table.optionsSharesRegistrationId.asc().nullsLast().op('uuid_ops'),
        ),
        idxOsrfdOsridFileid: uniqueIndex('idx_osrfd_osrid_fileid').using(
          'btree',
          table.optionsSharesRegistrationId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        optionsSharesRegistrationDOptionsSharesRegistrationFkey: foreignKey({
          columns: [table.optionsSharesRegistrationId],
          foreignColumns: [optionsSharesRegistrations.id],
          name: 'options_shares_registration_d_options_shares_registration__fkey',
        }),
        optionsSharesRegistrationDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'options_shares_registration_documents_file_id_fkey',
        }),
      },
    ];
  },
);

export const optionsSharesSubscriberRepresentatives = pgTable(
  'options_shares_subscriber_representatives',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    optionsSharesSubscriberId: uuid('options_shares_subscriber_id').notNull(),
    identityId: uuid('identity_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxOssrIdentityId: index('idx_ossr_identity_id').using(
          'btree',
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        idxOssrOssId: index('idx_ossr_oss_id').using(
          'btree',
          table.optionsSharesSubscriberId.asc().nullsLast().op('uuid_ops'),
        ),
        optionsSharesSubscriberRepOptionsSharesSubscriberIdFkey: foreignKey({
          columns: [table.optionsSharesSubscriberId],
          foreignColumns: [optionsSharesSubscribers.id],
          name: 'options_shares_subscriber_rep_options_shares_subscriber_id_fkey',
        }),
        optionsSharesSubscriberRepresentativesIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'options_shares_subscriber_representatives_identity_id_fkey',
        }),
      },
    ];
  },
);

export const shareIssueParticipantDocuments = pgTable(
  'share_issue_participant_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .notNull(),
    shareIssueParticipantId: uuid('share_issue_participant_id').notNull(),
    fileId: uuid('file_id').notNull(),
    kind: text().notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxSipdFileid: uniqueIndex('idx_sipd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxSipdSipid: index('idx_sipd_sipid').using(
          'btree',
          table.shareIssueParticipantId.asc().nullsLast().op('uuid_ops'),
        ),
        shareIssueParticipantDocumenShareIssueParticipantIdFkey: foreignKey({
          columns: [table.shareIssueParticipantId],
          foreignColumns: [shareIssueParticipants.id],
          name: 'share_issue_participant_documen_share_issue_participant_id_fkey',
        }),
        shareIssueParticipantDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'share_issue_participant_documents_file_id_fkey',
        }),
      },
    ];
  },
);

export const optionsSharesRegistrations = pgTable(
  'options_shares_registrations',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    snrCaseId: uuid('snr_case_id'),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    description: varchar(),
    payBeforeDate: date('pay_before_date'),
    ibanNumber: varchar('iban_number'),
    ibanBic: varchar('iban_bic'),
    ibanBank: varchar('iban_bank'),
    ibanAddress: varchar('iban_address'),
    paymentAccountKind: integer('payment_account_kind'),
    paymentName: varchar('payment_name'),
    paymentAccountNumber: varchar('payment_account_number'),
    signatoryId: uuid('signatory_id'),
    contactId: uuid('contact_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    recordDay: date('record_day'),
    regFormPaymentThroughBolago: boolean('reg_form_payment_through_bolago').default(false),
  },
  table => {
    return [
      {
        optionsSharesRegistrationsContactIdFkey: foreignKey({
          columns: [table.contactId],
          foreignColumns: [identities.id],
          name: 'options_shares_registrations_contact_id_fkey',
        }),
        optionsSharesRegistrationsSignatoryIdFkey: foreignKey({
          columns: [table.signatoryId],
          foreignColumns: [identities.id],
          name: 'options_shares_registrations_signatory_id_fkey',
        }),
        optionsSharesRegistrationsSnrCaseIdFkey: foreignKey({
          columns: [table.snrCaseId],
          foreignColumns: [snrCases.id],
          name: 'options_shares_registrations_snr_case_id_fkey',
        }),
        optionsSharesRegistrationsWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'options_shares_registrations_warrant_program_id_fkey',
        }),
      },
    ];
  },
);

export const shareIssueRegistrationDocuments = pgTable(
  'share_issue_registration_documents',
  {
    id: uuid().defaultRandom().notNull(),
    shareIssueRegistrationId: uuid('share_issue_registration_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxSirdFileid: uniqueIndex('idx_sird_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxSirdSirid: index('idx_sird_sirid').using(
          'btree',
          table.shareIssueRegistrationId.asc().nullsLast().op('uuid_ops'),
        ),
        idxSirdSiridFileid: uniqueIndex('idx_sird_sirid_fileid').using(
          'btree',
          table.shareIssueRegistrationId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        shareIssueRegistrationDocumShareIssueRegistrationIdFkey: foreignKey({
          columns: [table.shareIssueRegistrationId],
          foreignColumns: [shareIssueRegistrations.id],
          name: 'share_issue_registration_docum_share_issue_registration_id_fkey',
        }),
        shareIssueRegistrationDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'share_issue_registration_documents_file_id_fkey',
        }),
      },
    ];
  },
);

export const shareIssues = pgTable(
  'share_issues',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    pricePerShare: numeric('price_per_share'),
    maxNumberOfShares: numeric('max_number_of_shares'),
    valuationPreMoney: numeric('valuation_pre_money'),
    valuationPostMoney: numeric('valuation_post_money'),
    subscriptionStartDate: date('subscription_start_date'),
    subscriptionEndDate: date('subscription_end_date'),
    published: boolean().default(false).notNull(),
    restrictedToParticipants: boolean('restricted_to_participants').default(true).notNull(),
    companyId: uuid('company_id').notNull(),
    snrShareCapitalChangeId: uuid('snr_share_capital_change_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    description: varchar().notNull(),
    primaryLang: boolean('primary_lang').default(true).notNull(),
    secondaryLang: boolean('secondary_lang').default(false).notNull(),
    meetingId: uuid('meeting_id'),
    approvedByMeeting: boolean('approved_by_meeting'),
    numberOfSharesBefore: numeric('number_of_shares_before'),
    quotaValue: numeric('quota_value'),
    preferentialRights: boolean('preferential_rights').notNull(),
    delBoardsStatementOption: integer('__del_boards_statement_option'),
    boardsStatementDate: date('boards_statement_date'),
    boardsStatementSv: varchar('boards_statement_sv'),
    boardsStatementEn: varchar('boards_statement_en'),
    snrAuthorizationId: uuid('snr_authorization_id'),
    nonCashStatementPl: varchar('non_cash_statement_pl'),
    nonCashStatementSl: varchar('non_cash_statement_sl'),
    minNumberOfShares: numeric('min_number_of_shares').default('0').notNull(),
    shareClasses: jsonb('share_classes').default([]).notNull(),
    paymentKindCash: boolean('payment_kind_cash'),
    paymentKindOffset: boolean('payment_kind_offset'),
    paymentKindNonCash: boolean('payment_kind_non_cash'),
    paidForAt: timestamp('paid_for_at', { mode: 'string' }),
    stripeInvoiceId: varchar('stripe_invoice_id'),
    delSnrCaseId: uuid('_del_snr_case_id'),
    delPaymentDueDate: date('_del_payment_due_date'),
    delIbanNumber: varchar('_del_iban_number'),
    delIbanBic: varchar('_del_iban_bic'),
    delIbanBank: varchar('_del_iban_bank'),
    delIbanAddress: varchar('_del_iban_address'),
    delPaymentAccountKind: integer('_del_payment_account_kind').default(0),
    delPaymentName: varchar('_del_payment_name'),
    delPaymentAccountNumber: varchar('_del_payment_account_number'),
    delSignatoryId: uuid('_del_signatory_id'),
    delContactId: uuid('_del_contact_id'),
    currency: varchar().notNull(),
    purpose: text().notNull(),
    boardsStatementOption: varchar('boards_statement_option'),
  },
  table => {
    return [
      {
        idxSiCid: index('idx_si_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        shareIssuancesCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'share_issuances_company_id_fkey',
        }),
        shareIssuancesMeetingIdFkey: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'share_issuances_meeting_id_fkey',
        }),
        shareIssuancesShareCapitalChangeIdFkey: foreignKey({
          columns: [table.snrShareCapitalChangeId],
          foreignColumns: [snrShareCapitalChanges.id],
          name: 'share_issuances_share_capital_change_id_fkey',
        }),
        shareIssuancesSnrAuthorizationIdFkey: foreignKey({
          columns: [table.snrAuthorizationId],
          foreignColumns: [snrAuthorizations.id],
          name: 'share_issuances_snr_authorization_id_fkey',
        }),
        shareIssuesContactIdFkey: foreignKey({
          columns: [table.delContactId],
          foreignColumns: [identities.id],
          name: 'share_issues_contact_id_fkey',
        }),
        shareIssuesSignatoryIdFkey: foreignKey({
          columns: [table.delSignatoryId],
          foreignColumns: [identities.id],
          name: 'share_issues_signatory_id_fkey',
        }),
        shareIssuesSnrCaseIdFkey: foreignKey({
          columns: [table.delSnrCaseId],
          foreignColumns: [snrCases.id],
          name: 'share_issues_snr_case_id_fkey',
        }),
        checkLangIsSet: check('check_lang_is_set', sql`primary_lang OR secondary_lang`),
        chkSubEndDateGteSubStartDate: check(
          'chk_sub_end_date_gte_sub_start_date',
          sql`subscription_end_date >= subscription_start_date`,
        ),
      },
    ];
  },
);

export const shareIssueBoardMembers = pgTable(
  'share_issue_board_members',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    shareIssueId: uuid('share_issue_id'),
    identityId: uuid('identity_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxSibmSiidIid: uniqueIndex('idx_sibm_siid_iid').using(
          'btree',
          table.shareIssueId.asc().nullsLast().op('uuid_ops'),
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        shareIssuanceBoardMembersIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'share_issuance_board_members_identity_id_fkey',
        }),
        shareIssuanceBoardMembersShareIssuanceIdFkey: foreignKey({
          columns: [table.shareIssueId],
          foreignColumns: [shareIssues.id],
          name: 'share_issuance_board_members_share_issuance_id_fkey',
        }),
      },
    ];
  },
);

export const optionsSharesSubscriberDocuments = pgTable(
  'options_shares_subscriber_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .notNull(),
    optionsSharesSubscriberId: uuid('options_shares_subscriber_id').notNull(),
    fileId: uuid('file_id').notNull(),
    kind: text().notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxOssdFileid: uniqueIndex('idx_ossd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxOssdOssid: index('idx_ossd_ossid').using(
          'btree',
          table.optionsSharesSubscriberId.asc().nullsLast().op('uuid_ops'),
        ),
        optionsSharesSubscriberDocOptionsSharesSubscriberIdFkey: foreignKey({
          columns: [table.optionsSharesSubscriberId],
          foreignColumns: [optionsSharesSubscribers.id],
          name: 'options_shares_subscriber_doc_options_shares_subscriber_id_fkey',
        }),
        optionsSharesSubscriberDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'options_shares_subscriber_documents_file_id_fkey',
        }),
      },
    ];
  },
);

export const optionsSharesSubscribers = pgTable(
  'options_shares_subscribers',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    identityId: uuid('identity_id').notNull(),
    optionsSharesRegistrationId: uuid('options_shares_registration_id').notNull(),
    quantity: numeric().notNull(),
    shareClass: jsonb('share_class').notNull(),
    paymentStatus: integer('payment_status').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        optionsSharesRegistrationOptionsSharesRegistrationFkey1: foreignKey({
          columns: [table.optionsSharesRegistrationId],
          foreignColumns: [optionsSharesRegistrations.id],
          name: 'options_shares_registration__options_shares_registration__fkey1',
        }),
        optionsSharesRegistrationSubscribersIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'options_shares_registration_subscribers_identity_id_fkey',
        }),
      },
    ];
  },
);

export const optionsSubscriberRepresentatives = pgTable(
  'options_subscriber_representatives',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    warrantParticipantId: uuid('warrant_participant_id').notNull(),
    identityId: uuid('identity_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxOsrIdentityId: index('idx_osr_identity_id').using(
          'btree',
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        idxOsrOssId: index('idx_osr_oss_id').using(
          'btree',
          table.warrantParticipantId.asc().nullsLast().op('uuid_ops'),
        ),
        idxOsrWpidIdentityid: uniqueIndex('idx_osr_wpid_identityid').using(
          'btree',
          table.warrantParticipantId.asc().nullsLast().op('uuid_ops'),
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        optionsSubscriberRepresentativesIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'options_subscriber_representatives_identity_id_fkey',
        }),
        optionsSubscriberRepresentativesWarrantParticipantIdFkey: foreignKey({
          columns: [table.warrantParticipantId],
          foreignColumns: [warrantProgramParticipants.id],
          name: 'options_subscriber_representatives_warrant_participant_id_fkey',
        }),
      },
    ];
  },
);

export const shareIssueDocuments = pgTable(
  'share_issue_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    shareIssueId: uuid('share_issue_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxSidFileid: uniqueIndex('idx_sid_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxSidSiid: index('idx_sid_siid').using('btree', table.shareIssueId.asc().nullsLast().op('uuid_ops')),
        idxWpidDid: uniqueIndex('idx_wpid_did').using(
          'btree',
          table.shareIssueId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        shareIssuanceDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'share_issuance_documents_file_id_fkey',
        }),
        shareIssuanceDocumentsShareIssuanceIdFkey: foreignKey({
          columns: [table.shareIssueId],
          foreignColumns: [shareIssues.id],
          name: 'share_issuance_documents_share_issuance_id_fkey',
        }),
      },
    ];
  },
);

export const shareIssueRegistrations = pgTable(
  'share_issue_registrations',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    snrCaseId: uuid('snr_case_id'),
    shareIssueId: uuid('share_issue_id').notNull(),
    description: varchar().notNull(),
    ibanNumber: varchar('iban_number'),
    ibanBic: varchar('iban_bic'),
    ibanBank: varchar('iban_bank'),
    ibanAddress: varchar('iban_address'),
    paymentAccountKind: integer('payment_account_kind'),
    paymentName: varchar('payment_name'),
    paymentAccountNumber: varchar('payment_account_number'),
    paymentDueDate: date('payment_due_date'),
    subscribeEndDate: date('subscribe_end_date'),
    contactId: uuid('contact_id'),
    signatoryId: uuid('signatory_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    currencyExchangeRate: numeric('currency_exchange_rate'),
    regFormPaymentThroughBolago: boolean('reg_form_payment_through_bolago').default(false),
  },
  table => {
    return [
      {
        idxSirSiId: index('idx_sir_si_id').using('btree', table.shareIssueId.asc().nullsLast().op('uuid_ops')),
        shareIssueRegistrationsContactIdFkey: foreignKey({
          columns: [table.contactId],
          foreignColumns: [identities.id],
          name: 'share_issue_registrations_contact_id_fkey',
        }),
        shareIssueRegistrationsShareIssueIdFkey: foreignKey({
          columns: [table.shareIssueId],
          foreignColumns: [shareIssues.id],
          name: 'share_issue_registrations_share_issue_id_fkey',
        }),
        shareIssueRegistrationsSignatoryIdFkey: foreignKey({
          columns: [table.signatoryId],
          foreignColumns: [identities.id],
          name: 'share_issue_registrations_signatory_id_fkey',
        }),
        shareIssueRegistrationsSnrCaseIdFkey: foreignKey({
          columns: [table.snrCaseId],
          foreignColumns: [snrCases.id],
          name: 'share_issue_registrations_snr_case_id_fkey',
        }),
      },
    ];
  },
);

export const shareIssueParticipants = pgTable(
  'share_issue_participants',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    numberOfShares: numeric('number_of_shares'),
    numberOfSharesMax: numeric('number_of_shares_max'),
    identityId: uuid('identity_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    paymentStatus: integer('payment_status'),
    shareClass: jsonb('share_class'),
    paymentKind: integer('payment_kind').notNull(),
    claim: numeric(),
    proxyId: uuid('proxy_id'),
    shareIssueRegistrationId: uuid('share_issue_registration_id').notNull(),
  },
  table => {
    return [
      {
        idxSipSirId: index('idx_sip_sir_id').using(
          'btree',
          table.shareIssueRegistrationId.asc().nullsLast().op('uuid_ops'),
        ),
        idxSipsIdentityId: index('idx_sips_identity_id').using(
          'btree',
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        idxSipsProxyId: index('idx_sips_proxy_id').using('btree', table.proxyId.asc().nullsLast().op('uuid_ops')),
        shareIssuanceParticipantsIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'share_issuance_participants_identity_id_fkey',
        }),
        shareIssuanceParticipantsProxyIdFkey: foreignKey({
          columns: [table.proxyId],
          foreignColumns: [identities.id],
          name: 'share_issuance_participants_proxy_id_fkey',
        }),
        shareIssueParticipantsShareIssueRegistrationIdFkey: foreignKey({
          columns: [table.shareIssueRegistrationId],
          foreignColumns: [shareIssueRegistrations.id],
          name: 'share_issue_participants_share_issue_registration_id_fkey',
        }),
      },
    ];
  },
);

export const shareTransactions = pgTable(
  'share_transactions',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    companyId: uuid('company_id'),
    transactionDate: date('transaction_date'),
    serialNumber: integer('serial_number'),
    transactionType: integer('transaction_type'),
    name: varchar(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    blockStart: bigint('block_start', { mode: 'number' }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    blockEnd: bigint('block_end', { mode: 'number' }).notNull(),
    pricePerShare: numeric('price_per_share'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    shareClassFrom: jsonb('share_class_from'),
    shareClassTo: jsonb('share_class_to'),
    buyerId: uuid('buyer_id'),
    sellerId: uuid('seller_id'),
    snrShareCapitalChangeId: uuid('snr_share_capital_change_id'),
    shareCertificateDate: timestamp('share_certificate_date', { mode: 'string' }),
    comment: varchar(),
    shareTransactionGroupId: uuid('share_transaction_group_id'),
    rounded: text().notNull(),
  },
  table => {
    return [
      {
        idxShareTransactionsCidTdSn: uniqueIndex('idx_share_transactions_cid_td_sn').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
          table.transactionDate.asc().nullsLast().op('int4_ops'),
          table.serialNumber.asc().nullsLast().op('uuid_ops'),
        ),
        idxStsBuyerId: index('idx_sts_buyer_id').using('btree', table.buyerId.asc().nullsLast().op('uuid_ops')),
        idxStsSellerId: index('idx_sts_seller_id').using('btree', table.sellerId.asc().nullsLast().op('uuid_ops')),
        idxStsStgid: index('idx_sts_stgid').using(
          'btree',
          table.shareTransactionGroupId.asc().nullsLast().op('uuid_ops'),
        ),
        indexShareTransactionsOnCompanyId: index('index_share_transactions_on_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        shareTransactionsBuyerIdFkey: foreignKey({
          columns: [table.buyerId],
          foreignColumns: [shareholders.id],
          name: 'share_transactions_buyer_id_fkey',
        }),
        shareTransactionsSellerIdFkey: foreignKey({
          columns: [table.sellerId],
          foreignColumns: [shareholders.id],
          name: 'share_transactions_seller_id_fkey',
        }),
        shareTransactionsShareCapitalChangeIdFkey: foreignKey({
          columns: [table.snrShareCapitalChangeId],
          foreignColumns: [snrShareCapitalChanges.id],
          name: 'share_transactions_share_capital_change_id_fkey',
        }),
        shareTransactionsShareTransactionGroupIdFkey: foreignKey({
          columns: [table.shareTransactionGroupId],
          foreignColumns: [shareTransactionGroups.id],
          name: 'share_transactions_share_transaction_group_id_fkey',
        }),
        checkShareholders: check(
          'check_shareholders',
          sql`((transaction_type = 0) AND (buyer_id IS NOT NULL) AND (seller_id IS NULL)) OR ((transaction_type = 1) AND (buyer_id IS NOT NULL) AND (seller_id IS NOT NULL)) OR ((transaction_type = 2) AND (buyer_id IS NULL) AND (seller_id IS NOT NULL)) OR ((transaction_type = 3) AND (buyer_id IS NOT NULL) AND (seller_id IS NULL)) OR ((transaction_type = 4) AND (buyer_id IS NULL) AND (seller_id IS NOT NULL)) OR ((transaction_type = 5) AND (buyer_id IS NOT NULL) AND (seller_id IS NOT NULL))`,
        ),
      },
    ];
  },
);

export const shareTransactionDocuments = pgTable(
  'share_transaction_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    shareTransactionId: uuid('share_transaction_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxStdStidDid: uniqueIndex('idx_std_stid_did').using(
          'btree',
          table.shareTransactionId.asc().nullsLast().op('uuid_ops'),
          table.fileId.asc().nullsLast().op('uuid_ops'),
        ),
        shareTransactionDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'share_transaction_documents_file_id_fkey',
        }),
        shareTransactionDocumentsShareTransactionIdFkey: foreignKey({
          columns: [table.shareTransactionId],
          foreignColumns: [shareTransactions.id],
          name: 'share_transaction_documents_share_transaction_id_fkey',
        }),
      },
    ];
  },
);

export const snrAuthorizations = pgTable(
  'snr_authorizations',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    serialNumber: varchar('serial_number').notNull(),
    filingYear: varchar('filing_year').notNull(),
    filingNumber: varchar('filing_number').notNull(),
    registrationDate: date('registration_date').notNull(),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    authJson: jsonb('auth_json'),
  },
  table => {
    return [
      {
        fkRails767Eb69Fe7: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'fk_rails_767eb69fe7',
        }),
      },
    ];
  },
);

export const snrBonds = pgTable(
  'snr_bonds',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    filingYear: numeric('filing_year').notNull(),
    filingNumber: numeric('filing_number').notNull(),
    serialNumber: numeric('serial_number').notNull(),
    bondType: varchar('bond_type').notNull(),
    registrationDate: date('registration_date').notNull(),
    decisionDate: date('decision_date'),
    decisionBy: varchar('decision_by'),
    partRegistration: boolean('part_registration').notNull(),
    shareClass: varchar('share_class'),
    shareCapitalCanBeIncreaseBy: numeric('share_capital_can_be_increase_by'),
    shareCapitalCanBeIncreaseByCurrency: varchar('share_capital_can_be_increase_by_currency', { length: 8 }),
    freetext: text(),
    warrantSubFrom: date('warrant_sub_from'),
    warrantSubTo: date('warrant_sub_to'),
    numberOfOptions: numeric('number_of_options'),
    numberOfSharesMax: numeric('number_of_shares_max'),
    withoutLoan: boolean('without_loan'),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    shareSubStartDate: date('share_sub_start_date'),
    shareSubEndDate: date('share_sub_end_date'),
    numberOfOptionsMax: numeric('number_of_options_max'),
    numberOfOptionsMin: numeric('number_of_options_min'),
  },
  table => {
    return [
      {
        idxSnrBondsCompanyId: index('idx_snr_bonds_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        idxSnrBondsDiaryNumber: index('idx_snr_bonds_diary_number').using(
          'btree',
          table.filingYear.asc().nullsLast().op('numeric_ops'),
          table.filingNumber.asc().nullsLast().op('numeric_ops'),
          table.serialNumber.asc().nullsLast().op('numeric_ops'),
        ),
        snrBondsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'snr_bonds_company_id_fkey',
        }),
      },
    ];
  },
);

export const snrCases = pgTable(
  'snr_cases',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    arrivalDate: varchar('arrival_date'),
    registrationDate: varchar('registration_date'),
    filingNumber: varchar('filing_number').notNull(),
    filingYear: varchar('filing_year').notNull(),
    subjects: jsonb().notNull(),
    status: jsonb(),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        indexSnrCasesOnCompanyId: index('index_snr_cases_on_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        indexSnrCasesOnCompanyIdAndFilingYearAndFilingNumber: uniqueIndex(
          'index_snr_cases_on_company_id_and_filing_year_and_filing_number',
        ).using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
          table.filingYear.asc().nullsLast().op('uuid_ops'),
          table.filingNumber.asc().nullsLast().op('uuid_ops'),
        ),
        fkRailsA66Baa0434: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'fk_rails_a66baa0434',
        }),
      },
    ];
  },
);

export const snrDocuments = pgTable(
  'snr_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    diaryNumber: varchar('diary_number'),
    status: varchar(),
    finalRev: boolean('final_rev'),
    document: byteaAsBuffer('document'),
    companyId: uuid('company_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    ocrAvailable: boolean('ocr_available').default(false).notNull(),
    ocrData: jsonb('ocr_data'),
  },
  table => {
    return [
      {
        indexSnrDocumentsOnCompanyId: index('index_snr_documents_on_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        indexSnrDocumentsOnCompanyIdAndDiaryNumber: index('index_snr_documents_on_company_id_and_diary_number').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
          table.diaryNumber.asc().nullsLast().op('text_ops'),
        ),
        indexSnrDocumentsOnStatus: index('index_snr_documents_on_status').using(
          'btree',
          table.status.asc().nullsLast().op('text_ops'),
        ),
      },
    ];
  },
);

export const snrShareCapitalChanges = pgTable(
  'snr_share_capital_changes',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    serialNumber: numeric('serial_number'),
    filingYear: numeric('filing_year'),
    filingNumber: numeric('filing_number'),
    registrationDate: date('registration_date'),
    amountTotal: numeric('amount_total'),
    amountChange: numeric('amount_change'),
    snrReply: json('snr_reply'),
    companyId: uuid('company_id').notNull(),
    changeEkonv: json('change_ekonv'),
    changeFkonv: json('change_fkonv'),
    name: text(),
    sharesChange: numeric('shares_change'),
    sharesTotal: numeric('shares_total'),
    sharePriceFactor: numeric('share_price_factor'),
    sharesTotalSplitAdjusted: numeric('shares_total_split_adjusted'),
    transactionValue: numeric('transaction_value'),
    shareValue: numeric('share_value'),
    companyValue: numeric('company_value'),
    sharesTotalAfterChange: numeric('shares_total_after_change'),
    foundersShareFraction: numeric('founders_share_fraction'),
    sharesChangeSplitAdjusted: numeric('shares_change_split_adjusted'),
    companyValuePreMoney: numeric('company_value_pre_money'),
    amountTotalBeforeChange: numeric('amount_total_before_change'),
    shareValueSplitAdjusted: numeric('share_value_split_adjusted'),
    currency: varchar(),
    decisionDate: date('decision_date'),
    decisionMaker: varchar('decision_maker'),
    settledBy: json('settled_by'),
    shareClasses: jsonb('share_classes'),
    shareClassNameChanges: json('share_class_name_changes'),
    shareClassChanges: json('share_class_changes'),
    snrSplitFactor: numeric('snr_split_factor'),
    snrSplitSharesChange: numeric('snr_split_shares_change'),
    done: boolean().default(false).notNull(),
    decisionDateWarning: boolean('decision_date_warning').default(false).notNull(),
    correctedSplitFactor: numeric('corrected_split_factor'),
    correctedSplitSharesChange: numeric('corrected_split_shares_change'),
    sortOrderSnr: integer('sort_order_snr'),
    sortOrderOverride: integer('sort_order_override'),
    manualEntry: boolean('manual_entry').default(false).notNull(),
  },
  table => {
    return [
      {
        idxSccOnFilingYearAndFilingNumberAndSerialNumber: uniqueIndex(
          'idx_scc_on_filing_year_and_filing_number_and_serial_number',
        ).using(
          'btree',
          table.filingYear.asc().nullsLast().op('numeric_ops'),
          table.filingNumber.asc().nullsLast().op('numeric_ops'),
          table.serialNumber.asc().nullsLast().op('numeric_ops'),
        ),
        indexShareCapitalChangesOnCompanyId: index('index_share_capital_changes_on_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        fkRailsA1D8C6B15C: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'fk_rails_a1d8c6b15c',
        }),
      },
    ];
  },
);

export const stripePaymentConfigs = pgTable('stripe_payment_configs', {
  id: uuid()
    .default(sql`public.gen_random_uuid()`)
    .primaryKey()
    .notNull(),
  publishableKey: varchar('publishable_key').notNull(),
  secretKey: varchar('secret_key').notNull(),
  webhookSecret: varchar('webhook_secret').notNull(),
  createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
});

export const stripePayments = pgTable(
  'stripe_payments',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    amount: bigint({ mode: 'number' }).notNull(),
    currency: varchar().notNull(),
    charged: boolean().default(false).notNull(),
    source: jsonb(),
    charge: jsonb(),
    lastHookEvent: timestamp('last_hook_event', { mode: 'string' }),
    status: varchar(),
    failureCode: varchar('failure_code'),
    chargeId: varchar('charge_id'),
    cardPostfix: varchar('card_postfix'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    refunded: boolean().default(false).notNull(),
    disputed: boolean().default(false).notNull(),
  },
  () => {
    return [
      {
        stripePaymentsAmountCheck: check('stripe_payments_amount_check', sql`amount > 0`),
        stripePaymentsCurrencyCheck: check('stripe_payments_currency_check', sql`length((currency)::text) > 0`),
      },
    ];
  },
);

export const todos = pgTable(
  'todos',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    descriptionPrimaryLang: text('description_primary_lang'),
    dueDate: date('due_date'),
    done: boolean().default(false).notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    descriptionSecondaryLang: text('description_secondary_lang'),
    identityId: uuid('identity_id'),
  },
  table => {
    return [
      {
        todosIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'todos_identity_id_fkey',
        }),
      },
    ];
  },
);

export const snrOcrExtracts = pgTable(
  'snr_ocr_extracts',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    type: integer().notNull(),
    snrDocumentId: uuid('snr_document_id').notNull(),
    data: jsonb().notNull(),
  },
  table => {
    return [
      {
        fkRails79E1A5D212: foreignKey({
          columns: [table.snrDocumentId],
          foreignColumns: [snrDocuments.id],
          name: 'fk_rails_79e1a5d212',
        }),
      },
    ];
  },
);

export const shareTransactionGroups = pgTable(
  'share_transaction_groups',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    delKind: integer('__del_kind'),
    transactionDate: date('transaction_date').notNull(),
    sortOrder: integer('sort_order').notNull(),
    companyId: uuid('company_id').notNull(),
    snrShareCapitalChangeId: uuid('snr_share_capital_change_id'),
    shareIssueId: uuid('share_issue_id'),
    warrantProgramId: uuid('warrant_program_id'),
    splitFactor: numeric('split_factor'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    kind: varchar().notNull(),
  },
  table => {
    return [
      {
        idxStgCid: index('idx_stg_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        idxStgSiid: index('idx_stg_siid').using('btree', table.shareIssueId.asc().nullsLast().op('uuid_ops')),
        idxStgSsccid: index('idx_stg_ssccid').using(
          'btree',
          table.snrShareCapitalChangeId.asc().nullsLast().op('uuid_ops'),
        ),
        shareTransactionGroupsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'share_transaction_groups_company_id_fkey',
        }),
        shareTransactionGroupsShareIssueIdFkey: foreignKey({
          columns: [table.shareIssueId],
          foreignColumns: [shareIssues.id],
          name: 'share_transaction_groups_share_issue_id_fkey',
        }),
        shareTransactionGroupsSnrShareCapitalChangeIdFkey: foreignKey({
          columns: [table.snrShareCapitalChangeId],
          foreignColumns: [snrShareCapitalChanges.id],
          name: 'share_transaction_groups_snr_share_capital_change_id_fkey',
        }),
        shareTransactionGroupsWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'share_transaction_groups_warrant_program_id_fkey',
        }),
        stgTdSo: unique('stg_td_so').on(table.transactionDate, table.sortOrder, table.companyId),
      },
    ];
  },
);

export const shareStates = pgTable(
  'share_states',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    numberOfShares: numeric('number_of_shares', { precision: 38, scale: 0 }).notNull(),
    shareClass: varchar('share_class'),
    juridicalEntityId: uuid('juridical_entity_id').notNull(),
    snrShareCapitalChangeId: uuid('snr_share_capital_change_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).notNull(),
  },
  table => {
    return [
      {
        indexShareStatesOnJuridicalEntityId: index('index_share_states_on_juridical_entity_id').using(
          'btree',
          table.juridicalEntityId.asc().nullsLast().op('uuid_ops'),
        ),
        indexShareStatesOnShareCapitalChangeId: index('index_share_states_on_share_capital_change_id').using(
          'btree',
          table.snrShareCapitalChangeId.asc().nullsLast().op('uuid_ops'),
        ),
      },
    ];
  },
);

export const shareSubscriptions = pgTable(
  'share_subscriptions',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    companyId: uuid('company_id').notNull(),
    companyName: varchar('company_name', { length: 400 }),
    address: varchar({ length: 1024 }),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    fullName: varchar('full_name', { length: 256 }),
    referenceNumber: varchar('reference_number', { length: 20 }).notNull(),
    identityNumber: varchar('identity_number', { length: 32 }),
    email: varchar({ length: 256 }).notNull(),
    mandrillEvent: jsonb('mandrill_event'),
    numberOfShares: numeric('number_of_shares'),
    imported: boolean().default(false).notNull(),
    emailSent: boolean('email_sent').default(false).notNull(),
    emailNoticeSent: boolean('email_notice_sent').default(false).notNull(),
    emailReminderSent: boolean('email_reminder_sent').default(false).notNull(),
  },
  table => {
    return [
      {
        idxShareSubscriptionsCompany: index('idx_share_subscriptions_company').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        shareSubscriptionsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'share_subscriptions_company_id_fkey',
        }),
      },
    ];
  },
);

export const webAuthnKeys = pgTable(
  'web_authn_keys',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    userId: uuid('user_id').notNull(),
    displayName: varchar('display_name').notNull(),
    keyId: bytea('key_id').notNull(),
    publicKey: bytea('public_key').notNull(),
    attestationType: varchar('attestation_type').notNull(),
    authenticatorAaguid: bytea('authenticator_aaguid').notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    signCount: bigint('sign_count', { mode: 'number' }).notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxUidKi: uniqueIndex('idx_uid_ki').using(
          'btree',
          table.userId.asc().nullsLast().op('bytea_ops'),
          table.keyId.asc().nullsLast().op('bytea_ops'),
        ),
        idxUidPk: uniqueIndex('idx_uid_pk').using(
          'btree',
          table.userId.asc().nullsLast().op('uuid_ops'),
          table.publicKey.asc().nullsLast().op('uuid_ops'),
        ),
        webAuthnKeysUserIdFkey: foreignKey({
          columns: [table.userId],
          foreignColumns: [users.id],
          name: 'web_authn_keys_user_id_fkey',
        }),
      },
    ];
  },
);

export const wiseDependencyBonds = pgTable(
  'wise_dependency_bonds',
  {
    id: uuid()
      .default(sql`uuid_generate_v4()`)
      .primaryKey()
      .notNull(),
    name: varchar().notNull(),
    numberOfOptions: numeric('number_of_options').notNull(),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    snrBondId: uuid('snr_bond_id'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    kind: varchar(),
  },
  table => {
    return [
      {
        idxWdbSnrBondIdWpId: uniqueIndex('idx_wdb_snr_bond_id_wp_id').using(
          'btree',
          table.snrBondId.asc().nullsLast().op('uuid_ops'),
          table.warrantProgramId.asc().nullsLast().op('uuid_ops'),
        ),
        idxWiseDependencyBondsSnrBondId: index('idx_wise_dependency_bonds_snr_bond_id').using(
          'btree',
          table.snrBondId.asc().nullsLast().op('uuid_ops'),
        ),
        idxWiseDependencyBondsWpId: index('idx_wise_dependency_bonds_wp_id').using(
          'btree',
          table.warrantProgramId.asc().nullsLast().op('uuid_ops'),
        ),
        wiseDependencyBondsSnrBondIdFkey: foreignKey({
          columns: [table.snrBondId],
          foreignColumns: [snrBonds.id],
          name: 'wise_dependency_bonds_snr_bond_id_fkey',
        }),
        wiseDependencyBondsWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'wise_dependency_bonds_warrant_program_id_fkey',
        }),
      },
    ];
  },
);

export const warrantBoardMembers = pgTable(
  'warrant_board_members',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    identityId: uuid('identity_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxWbmWpidIid: uniqueIndex('idx_wbm_wpid_iid').using(
          'btree',
          table.warrantProgramId.asc().nullsLast().op('uuid_ops'),
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        warrantBoardMembersIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'warrant_board_members_identity_id_fkey',
        }),
        warrantBoardMembersWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'warrant_board_members_warrant_program_id_fkey',
        }),
      },
    ];
  },
);

export const meetingAgendaTemplateItems = pgTable(
  'meeting_agenda_template_items',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    order: integer().notNull(),
    subjectPrimaryLang: varchar('subject_primary_lang'),
    subjectSecondaryLang: varchar('subject_secondary_lang'),
    commentPrimaryLang: text('comment_primary_lang'),
    commentSecondaryLang: text('comment_secondary_lang'),
    decisionPrimaryLang: text('decision_primary_lang'),
    decisionSecondaryLang: text('decision_secondary_lang'),
    mandatory: boolean().notNull(),
    firstGroup: boolean('first_group').notNull(),
    lastGroup: boolean('last_group').notNull(),
    kind: integer().notNull(),
    decisionSuggestions: jsonb('decision_suggestions'),
    meetingAgendaTemplateId: uuid('meeting_agenda_template_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxMatMatid: index('idx_mat_matid').using(
          'btree',
          table.meetingAgendaTemplateId.asc().nullsLast().op('uuid_ops'),
        ),
        meetingAgendaTemplateItemsMeetingAgendaTemplateIdFkey: foreignKey({
          columns: [table.meetingAgendaTemplateId],
          foreignColumns: [meetingAgendaTemplates.id],
          name: 'meeting_agenda_template_items_meeting_agenda_template_id_fkey',
        }),
        uniqueMatidOrder: unique('unique_matid_order').on(table.order, table.meetingAgendaTemplateId),
        checkGroups: check('check_groups', sql`NOT ((first_group = true) AND (last_group = true))`),
      },
    ];
  },
);

export const optionsRegistrations = pgTable(
  'options_registrations',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    snrCaseId: uuid('snr_case_id'),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    description: varchar(),
    payBeforeDate: date('pay_before_date'),
    ibanNumber: varchar('iban_number'),
    ibanBic: varchar('iban_bic'),
    ibanBank: varchar('iban_bank'),
    ibanAddress: varchar('iban_address'),
    paymentAccountKind: integer('payment_account_kind'),
    paymentName: varchar('payment_name'),
    paymentAccountNumber: varchar('payment_account_number'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    contactId: uuid('contact_id'),
    signatoryId: uuid('signatory_id'),
    regFormPaymentThroughBolago: boolean('reg_form_payment_through_bolago').default(false),
  },
  table => {
    return [
      {
        optionsRegistrationsContactIdFkey: foreignKey({
          columns: [table.contactId],
          foreignColumns: [identities.id],
          name: 'options_registrations_contact_id_fkey',
        }),
        optionsRegistrationsSignatoryIdFkey: foreignKey({
          columns: [table.signatoryId],
          foreignColumns: [identities.id],
          name: 'options_registrations_signatory_id_fkey',
        }),
        optionsRegistrationsSnrCaseIdFkey: foreignKey({
          columns: [table.snrCaseId],
          foreignColumns: [snrCases.id],
          name: 'options_registrations_snr_case_id_fkey',
        }),
        optionsRegistrationsWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'options_registrations_warrant_program_id_fkey',
        }),
      },
    ];
  },
);

export const schemaMigrations = pgTable('schema_migrations', {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  version: bigint({ mode: 'number' }).primaryKey().notNull(),
  name: text().default('').notNull(),
  up: text().default('').notNull(),
  down: text().default('').notNull(),
});
